import { InputWithLabel, SelectWithLabel } from '@adotel/components'
import {
  Button,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import SearchIcon from '@material-ui/icons/Search'
import { memo, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import Breadcrumbs from '../../components/Breadcrumb'
import { convertPersianNumbersToEnglishNumbers } from '../../helpers/numbers'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import roomTypePagesRoleManagement from '../../staticRoleManagement/roomTypeRoleManagement'
import {
  RoomTypeFilters,
  RoomTypeFiltersActivation,
  RoomTypeFiltersExisting,
  RoomTypeFiltersType
} from '../../types/roomtype'
import useStyles from './styles/RoomtypesHeader.styles'

interface SelectItems {
  value: string
  lable: string
}

interface RoomTypesHeaderProps {
  inputValue: string | undefined
  accommodationName: string
  setinputVal: (val: string) => void
  loading: boolean
  filters: RoomTypeFilters
  setFilters: React.Dispatch<React.SetStateAction<RoomTypeFilters>>
  isRoleAdmin: boolean
}

const RoomtypesHeader: React.FC<RoomTypesHeaderProps> = ({
  inputValue,
  setinputVal,
  accommodationName,
  loading,
  filters,
  setFilters,
  isRoleAdmin
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const params = useParams<{ accommodationID: string; propertyCode: string }>()

  const cachedLevels = useMemo(
    () => [
      { label: getTranslate('Accommodations'), link: '/accommodations' },
      {
        label: accommodationName,
        link: `/accommodations/${params.accommodationID}/${params.propertyCode}/details`
      },
      {
        label: getTranslate('room types')
      }
    ],
    [accommodationName]
  )

  const menuItemsForType: SelectItems[] = [
    { value: 'all', lable: 'All' },
    { value: 'virtual', lable: 'Virtual' },
    { value: 'physical', lable: 'Physical' }
  ]

  const menuItemsForExistance: SelectItems[] = [
    { value: 'all', lable: 'All' },
    { value: 'deleted', lable: 'Deleted' },
    { value: 'existing', lable: 'Existing' }
  ]

  const menuItemsForActivation: SelectItems[] = [
    { value: 'all', lable: 'All' },
    { value: 'active', lable: 'Active' },
    { value: 'deactive', lable: 'Deactive' }
  ]

  return (
    <>
      <div className={classes.backitemContainer}>
        <Breadcrumbs levels={cachedLevels} loading={loading} />
      </div>
      <div className={classes.header}>
        <Typography variant="h3" gutterBottom>
          {getTranslate(isSmallScreen ? 'Room Types' : 'Room Types List')}
        </Typography>
        {roomTypePagesRoleManagement
          .find(
            (item) =>
              item.route ===
              '/accommodations/:accommodationID/:propertyCode/room-types/create'
          )
          ?.rolesListWithWriteAccess?.includes(getRoleFromStorage()) && (
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            color="primary"
            component={LocalizedLink}
            to={`${location.pathname
              .replace('/list', '')
              .replace('/en', '')
              .replace('/fa', '')}/create`}>
            {getTranslate(isSmallScreen ? 'Add' : 'Add Roomtype')}
          </Button>
        )}
      </div>
      <div className={classes.root}>
        <InputWithLabel
          placeholder={getTranslate('Search by name, code or type')}
          autoComplete="off"
          noHelperText
          fullWidth
          startIcon={<SearchIcon />}
          className={classes.searchInput}
          value={inputValue}
          onChange={(event) => {
            setinputVal(
              convertPersianNumbersToEnglishNumbers(event.currentTarget.value)
            )
          }}
          inputSize="small"
        />
      </div>
      {isRoleAdmin && (
        <div className={classes.filters}>
          <SelectWithLabel
            fullWidth
            value={filters.type}
            onChange={(event) => {
              setFilters({
                ...filters,
                type: event.target.value as RoomTypeFiltersType
              })
            }}
            size="small"
            label={getTranslate('Type')}>
            {menuItemsForType.map((item, index) => (
              <MenuItem key={index} value={item.value}>
                {getTranslate(item.lable)}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <SelectWithLabel
            fullWidth
            value={filters.existing}
            onChange={(event) => {
              setFilters({
                ...filters,
                existing: event.target.value as RoomTypeFiltersExisting
              })
            }}
            size="small"
            label={getTranslate('Existance')}>
            {menuItemsForExistance.map((item, index) => (
              <MenuItem key={index} value={item.value}>
                {getTranslate(item.lable)}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <SelectWithLabel
            value={filters.activation}
            fullWidth
            onChange={(event) => {
              setFilters({
                ...filters,
                activation: event.target.value as RoomTypeFiltersActivation
              })
            }}
            size="small"
            label={getTranslate('Activation')}>
            {menuItemsForActivation.map((item, index) => (
              <MenuItem key={index} value={item.value}>
                {getTranslate(item.lable)}
              </MenuItem>
            ))}
          </SelectWithLabel>
        </div>
      )}
    </>
  )
}

export default memo(RoomtypesHeader)
